import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Project } from '../types';
import { useAuth } from '../contexts/AuthContext';
import {
  getAllProjects,
  createProject,
  updateProject,
  deleteProject,
  uploadProjectImage,
  deleteProjectImage
} from '../services/projectService';
import { v4 as uuidv4 } from 'uuid';
import {
  AdminContainer,
  AdminHeader,
  AdminTitle,
  AdminButton,
  ProjectsGrid,
  ProjectCard,
  ProjectImageContainer,
  ProjectImage,
  ProjectInfo,
  ProjectTitle,
  ProjectActions,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalFooter,
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  FormSelect,
  FormCheckbox,
  KeyPointsContainer,
  KeyPointItem,
  AddButton,
  FileUploadContainer,
  ImagePreviewContainer,
  ImagePreview,
  ErrorMessage,
  SuccessMessage,
  TabsContainer,
  TabButton,
  // Composants de galerie de détails supprimés
  // Composants d'images détaillées supprimés
  // Composants de détails du projet supprimés
} from '../styles/AdminStyles';

const projectCategories = [
  'Résidentiel',
  'Commercial',
  'Hôtellerie',
  'Restaurant',
  'Bureau',
  'Hôpital',
  'Événementiel',
  'Autre'
];

// Types de détails supprimés

const AdminPage: React.FC = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'projects' | 'settings'>('projects');
  const [projects, setProjects] = useState<Project[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [removedImageUrls, setRemovedImageUrls] = useState<string[]>([]);
  // État des images détaillées supprimé
  
  // Fonction de déconnexion
  const handleLogout = () => {
    // Utiliser le service d'authentification pour se déconnecter
    signOut();
    
    // Rediriger vers la page de connexion
    navigate('/login');
    
    // Afficher un message de succès si nécessaire
    // setSuccess('Déconnexion réussie');
  };
  // États liés aux détails du projet supprimés
  
  // Formulaire contrôlé - version simplifiée
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    description: '',
    mainImage: '',
    images: [] as string[],
    year: '',
    category: '',
    subcategory: '',
    isPublished: false,
    isFeaturedHome: false,
    position: 1,
    galleryPosition: 1
  });
  
  // Références vers les inputs de fichier
  const mainImageInputRef = useRef<HTMLInputElement>(null);
  const additionalImagesInputRef = useRef<HTMLInputElement>(null);
  
  // Chargement initial des projets
  useEffect(() => {
    loadProjects();
  }, []);
  
  // Chargement des projets
  const loadProjects = async () => {
    try {
      const data = await getAllProjects();
      setProjects(data);
    } catch (err) {
      console.error('Erreur lors du chargement des projets:', err);
      setError('Impossible de charger les projets. Veuillez réessayer.');
    }
  };
  
  // Fonctions de chargement des images détaillées et des détails du projet supprimées

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'position' ? parseInt(value, 10) : value }));
  };
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };
  
  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setMainImageFile(e.target.files[0]);
    }
  };
  
  const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      setImageFiles(prev => [...prev, ...filesArray]);
    }
  };
  
  const removeImage = (index: number) => {
    setImageFiles(prev => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };
  
  const openNewProjectModal = () => {
    setActiveProject(null);
    setFormData({
      title: '',
      location: '',
      description: '',
      mainImage: '',
      images: [],
      year: '',
      category: 'Résidentiel',
      subcategory: '',
      isPublished: false,
      isFeaturedHome: false,
      position: 1,
      galleryPosition: 1
    });
    setImageFiles([]);
    setMainImageFile(null);
    setRemovedImageUrls([]);
    setIsModalOpen(true);
  };
  
  const openEditProjectModal = (project: Project) => {
    setActiveProject(project);
    setFormData({
      title: project.title,
      location: project.location || '',
      description: project.description,
      mainImage: project.mainImage || '',
      images: project.images,
      year: project.year || '',
      category: project.category,
      subcategory: project.subcategory || '',
      isPublished: project.isPublished,
      isFeaturedHome: project.isFeaturedHome || false,
      position: project.position || 1,
      galleryPosition: project.galleryPosition || 1
    });
    setImageFiles([]);
    setMainImageFile(null);
    setRemovedImageUrls([]);
    setIsModalOpen(true);
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
    setActiveProject(null);
    // Références aux détails du projet supprimées
  };
  
  const filterProjects = (): Project[] => {
    let filteredProjects = [...projects];
    
    // Dans cette nouvelle version, activeTab est soit 'projects' soit 'settings'
    // Pas besoin de filtrer par statut de publication ici
    return filteredProjects;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsUploading(true);
      setError(null);
      
      // Création ou mise à jour du projet
      if (activeProject) {
        // Mise à jour d'un projet existant
        const projectId = activeProject.id as string;
        let updatedMainImage = formData.mainImage || '';
        // Upload de la nouvelle image principale si sélectionnée
        if (mainImageFile) {
          const mainImageUrl = await uploadProjectImage(mainImageFile, projectId, formData.title);
          updatedMainImage = mainImageUrl;
        }
        
        // Upload des images supplémentaires si elles ont été ajoutées
        if (imageFiles.length > 0) {
          const imageUrls = [];
          
          for (const file of imageFiles) {
            const imageUrl = await uploadProjectImage(file, projectId, formData.title);
            imageUrls.push(imageUrl);
          }
          
          formData.images = [...formData.images, ...imageUrls];
        }
        // Appliquer les suppressions marquées
        const filteredImages = formData.images.filter(img => !removedImageUrls.includes(img));
        let finalMainImage = updatedMainImage;
        // Si l'image principale a été supprimée, choisir une nouvelle principale
        if (finalMainImage && removedImageUrls.includes(finalMainImage)) {
          finalMainImage = filteredImages[0] || '';
        }
        // Mettre à jour le projet avec images filtrées et mainImage
        await updateProject(projectId, {
          ...formData,
          images: filteredImages,
          mainImage: finalMainImage
        });
        // Supprimer dans le stockage les images marquées
        for (const url of removedImageUrls) {
          await deleteProjectImage(url);
        }
        
        // Les images détaillées ont été supprimées
      } else {
        // Création d'un nouveau projet
        const newProjectId = uuidv4();
        
        // Upload de l'image principale
        let mainImageUrl = '';
        if (mainImageFile) {
          mainImageUrl = await uploadProjectImage(mainImageFile, newProjectId, formData.title);
        }
        
        // Upload des images supplémentaires
        const imageUrls = [];
        for (const file of imageFiles) {
          const imageUrl = await uploadProjectImage(file, newProjectId, formData.title);
          imageUrls.push(imageUrl);
        }
        
        // Créer le projet avec l'image principale
        const newProject = await createProject({
          ...formData,
          mainImage: mainImageUrl,
          images: imageUrls,
        });
        
        // Les images détaillées ont été supprimées
      }
      
      await loadProjects();
      setSuccess('Projet mis à jour avec succès');
      setTimeout(() => setSuccess(null), 3000);
      closeModal();
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleDeleteProject = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) {
      try {
        await deleteProject(id);
        await loadProjects();
        setSuccess('Projet supprimé avec succès');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError('Erreur lors de la suppression du projet');
        console.error(err);
      }
    }
  };
  
  // Fonction de mise à jour de légende d'image détaillée supprimée
  
  // Fonction de suppression d'image détaillée supprimée

  return (
    <AdminContainer>
      <AdminHeader>
        <AdminTitle>Gestion de la Galerie</AdminTitle>
        <div style={{ display: 'flex', gap: '10px' }}>
          <AdminButton onClick={openNewProjectModal}>Ajouter un projet</AdminButton>
          <AdminButton 
            onClick={handleLogout}
            style={{ backgroundColor: '#f44336' }}
          >
            Se déconnecter
          </AdminButton>
        </div>
      </AdminHeader>
      
      <TabsContainer>
        <TabButton 
          $isActive={activeTab === 'projects'} 
          onClick={() => setActiveTab('projects')}
        >
          Galerie
        </TabButton>
        <Link to="/admin/catalog" style={{ textDecoration: 'none' }}>
          <TabButton $isActive={false}>
            Catalogue
          </TabButton>
        </Link>
      </TabsContainer>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
      
      {projects.length === 0 ? (
        <p>Aucun projet trouvé.</p>
      ) : (
        <ProjectsGrid>
          {filterProjects().map(project => (
            <ProjectCard key={project.id}>
              <ProjectImageContainer>
                <ProjectImage 
                  src={project.mainImage || '/images/placeholder.jpg'} 
                  alt={project.title} 
                />
              </ProjectImageContainer>
              <ProjectInfo>
                <ProjectTitle>{project.title}</ProjectTitle>
                <div>{project.isPublished ? 'Publié' : 'Brouillon'}</div>
                <ProjectActions>
                  <AdminButton onClick={() => openEditProjectModal(project)}>
                    Modifier
                  </AdminButton>
                  <AdminButton onClick={() => handleDeleteProject(project.id!)}>
                    Supprimer
                  </AdminButton>
                </ProjectActions>
              </ProjectInfo>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      )}
      
      {isModalOpen && (
        <ModalOverlay>
          <ModalContent>
            <ModalCloseButton onClick={closeModal}>&times;</ModalCloseButton>
            <h2>{activeProject ? 'Modifier le projet' : 'Ajouter un projet'}</h2>
            
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel htmlFor="title">Titre du projet*</FormLabel>
                <FormInput
                  id="title"
                  name="title"
                  value={formData.title || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="location">Localisation</FormLabel>
                <FormInput
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location || ''}
                  onChange={handleInputChange}
                  placeholder="Ex: Paris, Lyon, etc."
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="description">Description</FormLabel>
                <FormTextarea
                  id="description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              {/* Section des points clés supprimée */}
              
              {/* Champ année supprimé */}
              
              <FormGroup>
                <FormLabel htmlFor="category">Catégorie</FormLabel>
                <FormSelect
                  id="category"
                  name="category"
                  value={formData.category || 'Résidentiel'}
                  onChange={handleInputChange}
                >
                  {projectCategories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </FormSelect>
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="subcategory">Sous-catégorie (optionnel)</FormLabel>
                <FormInput
                  type="text"
                  id="subcategory"
                  name="subcategory"
                  value={formData.subcategory || ''}
                  onChange={handleInputChange}
                  placeholder="ex: Brasserie classique, Appartement haussmannien..."
                />
                <small style={{ marginTop: '5px', display: 'block', opacity: 0.7 }}>
                  Texte affiché après la catégorie (ex: "Restaurant, Brasserie classique")
                </small>
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="position">Position sur la page d'accueil (0-9)</FormLabel>
                <FormSelect
                  id="position"
                  name="position"
                  value={formData.position !== undefined ? formData.position : 1}
                  onChange={handleInputChange}
                >
                  <option key={0} value={0}>0 - Ne pas afficher sur la page d'accueil</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(pos => (
                    <option key={pos} value={pos}>
                      {pos}
                    </option>
                  ))}
                </FormSelect>
                <small style={{ marginTop: '5px', display: 'block', opacity: 0.7 }}>
                  Position 0 = Ne pas afficher sur la page d'accueil<br />
                  Positions 1-9 = Définit l'ordre d'apparition dans la galerie de la page d'accueil (1 = premier)
                </small>
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="galleryPosition">Position dans la page galerie</FormLabel>
                <FormInput
                  type="number"
                  id="galleryPosition"
                  name="galleryPosition"
                  value={formData.galleryPosition !== undefined ? formData.galleryPosition : 1}
                  onChange={handleInputChange}
                  min="1"
                  max="999"
                />
                <small style={{ marginTop: '5px', display: 'block', opacity: 0.7 }}>
                  Définit l'ordre d'apparition dans la page galerie complète (1 = premier, 2 = deuxième, etc.)
                </small>
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Image principale*</FormLabel>
                {formData.mainImage && !mainImageFile && (
                  <ImagePreviewContainer>
                    <ImagePreview>
                      <img src={formData.mainImage} alt="Aperçu" />
                    </ImagePreview>
                  </ImagePreviewContainer>
                )}
                {mainImageFile && (
                  <ImagePreviewContainer>
                    <ImagePreview>
                      <img 
                        src={URL.createObjectURL(mainImageFile)} 
                        alt="Aperçu" 
                      />
                      <button 
                        type="button" 
                        onClick={() => setMainImageFile(null)}
                      >
                        ✕
                      </button>
                    </ImagePreview>
                  </ImagePreviewContainer>
                )}
                <FileUploadContainer onClick={() => mainImageInputRef.current?.click()}>
                  <p>Cliquez ou glissez-déposez pour ajouter une image principale</p>
                  <input
                    type="file"
                    ref={mainImageInputRef}
                    onChange={handleMainImageChange}
                    accept="image/*"
                  />
                </FileUploadContainer>
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Images additionnelles</FormLabel>
                {formData.images && formData.images.length > 0 && (
                  <ImagePreviewContainer>
                    {formData.images.map((img, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <ImagePreview>
                          <img src={img} alt={`Élément ${index + 1}`} />
                        </ImagePreview>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                          <button type="button" onClick={() => setFormData(prev => ({ ...prev, mainImage: img }))}>
                            Définir principale
                          </button>
                          <button
                            type="button"
                            onClick={() => setRemovedImageUrls(prev => prev.includes(img) ? prev.filter(u => u !== img) : [...prev, img])}
                            style={{ backgroundColor: removedImageUrls.includes(img) ? '#f44336' : undefined, color: removedImageUrls.includes(img) ? 'white' : undefined }}
                          >
                            {removedImageUrls.includes(img) ? 'Annuler suppression' : 'Supprimer'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </ImagePreviewContainer>
                )}
                {imageFiles.length > 0 && (
                  <ImagePreviewContainer>
                    {imageFiles.map((file, index) => (
                      <ImagePreview key={index}>
                        <img 
                          src={URL.createObjectURL(file)} 
                          alt={`Élément ${index + 1}`} 
                        />
                        <button 
                          type="button" 
                          onClick={() => removeImage(index)}
                        >
                          ✕
                        </button>
                      </ImagePreview>
                    ))}
                  </ImagePreviewContainer>
                )}
                <FileUploadContainer onClick={() => additionalImagesInputRef.current?.click()}>
                  <p>Cliquez ou glissez-déposez pour ajouter des images</p>
                  <input
                    type="file"
                    multiple
                    ref={additionalImagesInputRef}
                    onChange={handleImagesChange}
                    accept="image/*"
                  />
                </FileUploadContainer>
              </FormGroup>
              
              {/* Section des images détaillées supprimée */}
              
              <FormGroup>
                <FormCheckbox>
                  <input
                    type="checkbox"
                    id="isPublished"
                    name="isPublished"
                    checked={formData.isPublished || false}
                    onChange={handleCheckboxChange}
                  />
                  <FormLabel htmlFor="isPublished">Publier ce projet</FormLabel>
                </FormCheckbox>
              </FormGroup>
              
              <ModalFooter>
                <AdminButton type="button" onClick={closeModal}>
                  Annuler
                </AdminButton>
                <AdminButton 
                  type="submit" 
                  disabled={isUploading}
                >
                  {isUploading ? 'Enregistrement...' : 'Enregistrer'}
                </AdminButton>
              </ModalFooter>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AdminContainer>
  );
};

export default AdminPage;
